import { useState } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type LegalEntityFormSchema } from '@webapp/platform/legal-entities/components/LegalEntityForm'
import { useLegalEntityUniquenessChecks } from '@webapp/platform/legal-entities/hooks/useLegalEntityUniquenessChecks'
import {
  CREATE_LEGAL_ENTITY_STEPS,
  type CreateLegalEntityStep,
} from './CreateLegalEntityStep'
import messages from './messages'
import { useMutateCreateLegalEntity } from '../../models/useMutateCreateLegalEntity'

const defaultFormValues: LegalEntityFormSchema = {
  name: '',
  country: null,
  externalId: '',
  baseCurrency: null,
}

interface UseCreateLegalEntityFormPresenterParams {
  onSuccess?: () => void
}

/**
 * Orchestrates the two-step create wizard (form -> review -> confirm).
 *
 * The form step only collects values; the LegalEntity is persisted on Confirm
 * in the review step.
 */
export const useCreateLegalEntityFormPresenter = ({
  onSuccess,
}: UseCreateLegalEntityFormPresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutateAsync, isLoading } = useMutateCreateLegalEntity()

  // Name + External ID are unique per org; surfaced as inline errors on Create.
  // The server re-checks the name on Confirm as the authoritative guard (there
  // is no server-side guard for external ID).
  const { isNameTaken, isExternalIdTaken } = useLegalEntityUniquenessChecks()

  const [step, setStep] = useState<CreateLegalEntityStep>(
    CREATE_LEGAL_ENTITY_STEPS.FORM
  )
  const [formValues, setFormValues] =
    useState<LegalEntityFormSchema>(defaultFormValues)

  const handleContinue = (values: LegalEntityFormSchema) => {
    setFormValues(values)
    setStep(CREATE_LEGAL_ENTITY_STEPS.REVIEW)
  }

  const handleEdit = () => {
    setStep(CREATE_LEGAL_ENTITY_STEPS.FORM)
  }

  const handleConfirm = async () => {
    if (!formValues.baseCurrency) {
      setStep(CREATE_LEGAL_ENTITY_STEPS.FORM)

      return
    }

    try {
      await mutateAsync({
        name: formValues.name,
        baseCurrencyId: formValues.baseCurrency.id,
        country: formValues.country?.name,
        externalId: formValues.externalId,
      })

      // Success feedback (toast) and modal close are owned by the hosting
      // FeatureSet via onSuccess — see useLegalEntityListFeatureSet.
      onSuccess?.()
    } catch (err) {
      // The presenter owns the error toast (ADR-10).
      toast.error({
        title: intl.formatMessage(messages.errorToastTitle),
        children: getMessageFromError(err),
      })
    }
  }

  return {
    state: {
      step,
      formValues,
      isConfirming: isLoading,
      reviewTitle: formValues.name,
      baseCurrencyName: formValues.baseCurrency?.name ?? '',
      reviewCountry: formValues.country?.name ?? '',
    },
    methods: {
      handleContinue,
      handleEdit,
      handleConfirm,
      isNameTaken,
      isExternalIdTaken,
    },
  }
}
